import {
	BingAdsCampaignDTO,
	FacebookAdsCampaignDTO,
	GoogleAdsCampaignDTO
} from 'api-models'

/**
 * Shared campaign type used across the breadcrumb selectors. Campaigns of all
 * three platforms (Google, Meta, Microsoft) flow through the asset store at
 * runtime, so the union must cover Bing as well.
 */
export type CampaignDTO =
	| GoogleAdsCampaignDTO
	| FacebookAdsCampaignDTO
	| BingAdsCampaignDTO

/**
 * Every bucket the platforms' statuses normalise into. `CampaignStatusGroup` is the
 * name DEV-2422's Connections code uses; the breadcrumb pills (DEV-2457) offer all
 * three, so the two are the same union.
 */
export type CampaignStatusGroup = 'ACTIVE' | 'PAUSED' | 'REMOVED'

/** The two buckets the breadcrumb pills offer. */
export type CampaignStatusFilter = 'ACTIVE' | 'PAUSED' | 'REMOVED'

export const CAMPAIGN_STATUS_FILTERS: CampaignStatusFilter[] = [
	'ACTIVE',
	'PAUSED',
	'REMOVED'
]

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatusFilter, string> = {
	ACTIVE: 'Active',
	PAUSED: 'Paused',
	REMOVED: 'Removed'
}

/**
 * Normalises the per-platform status enums into the two buckets the UI cares
 * about. Statuses that are neither active nor paused (removed, deleted,
 * archived, suspended, in-process, ...) return `null` and are not covered by
 * either filter pill.
 *
 * - Google: ENABLED -> Active, PAUSED -> Paused, REMOVED -> Removed, REMOVED -> Removed
 * - Meta:   ACTIVE  -> Active, PAUSED -> Paused, DELETED / ARCHIVED -> Removed, DELETED / ARCHIVED -> Removed
 * - Bing:   Active  -> Active, Paused / BudgetPaused / BudgetAndManualPaused -> Paused,
 *           Deleted -> Removed,
 *           Deleted / Suspended -> Removed
 */
export const getCampaignStatusGroup = (
	campaign: CampaignDTO
): CampaignStatusGroup | null => {
	const status = (campaign as { status?: string | null }).status
	if (!status) return null
	const normalized = String(status).toUpperCase()
	if (normalized === 'ENABLED' || normalized === 'ACTIVE') return 'ACTIVE'
	if (
		normalized === 'PAUSED' ||
		normalized === 'BUDGETPAUSED' ||
		normalized === 'BUDGETANDMANUALPAUSED'
	) {
		return 'PAUSED'
	}
	if (
		normalized === 'REMOVED' ||
		normalized === 'DELETED' ||
		normalized === 'ARCHIVED'
	) {
		return 'REMOVED'
	}
	if (
		normalized === 'REMOVED' ||
		normalized === 'DELETED' ||
		normalized === 'ARCHIVED' ||
		normalized === 'SUSPENDED'
	) {
		return 'REMOVED'
	}
	return null
}

/** Campaigns whose status falls into one of the given groups. */
export const filterCampaignsByStatus = <T extends CampaignDTO>(
	campaigns: T[],
	statuses: CampaignStatusGroup[]
): T[] => {
	if (statuses.length === 0) return []
	return campaigns.filter((c) => {
		const group = getCampaignStatusGroup(c)
		return group !== null && statuses.includes(group)
	})
}

/** Count of campaigns per status group. */
export const countCampaignsByStatus = (
	campaigns: CampaignDTO[]
): Record<CampaignStatusFilter, number> => {
	const counts: Record<CampaignStatusFilter, number> = {
		ACTIVE: 0,
		PAUSED: 0,
		REMOVED: 0
	}
	campaigns.forEach((c) => {
		const group = getCampaignStatusGroup(c)
		// `REMOVED` has no pill, so it has no counter to land in.
		if (group === 'ACTIVE' || group === 'PAUSED') counts[group] += 1
	})
	return counts
}

/**
 * The default selection on a fresh page load: every campaign, regardless of
 * status (DEV-2437 — previously Active only). Returns their ids (deduped) so
 * consumers (reporting + URL params) can scope to them.
 */
export const getAllCampaignIds = (campaigns: CampaignDTO[]): string[] => [
	...new Set(campaigns.map((c) => c.id).filter(Boolean))
]
